import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { EventBusyRounded } from "@mui/icons-material";
import { formatDateMMDDyyyy } from "../../../util/DateUtils";

interface LocationAtCapacityNoticeProps {
  /** Date (ISO or parseable string) the location is expected to reopen; null when unknown. */
  nextAvailableDate?: string | null;
  /** First day in the buyer's requested dates without room; switches the notice to the booking-form wording. */
  unavailableDate?: string | null;
  /** Admin brokered bookings may overbook, so the booking-form wording warns instead of blocking. */
  isBrokeredBooking?: boolean;
}

const NoticeRoot = styled(Box)({
  background: "#F0F0F0",
  border: "1px solid #BDBDBD",
  borderRadius: 4,
  padding: "8px 12px",
  fontSize: "0.8571rem", // 12px at 14px root
  color: "#424242",
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 8,
});

/**
 * Gray informational bar shown on a buyer-facing location card when the
 * location has zero available capacity today. Tells the buyer the location
 * is at capacity and, when known, the date it reopens.
 */
const LocationAtCapacityNotice = ({ nextAvailableDate, unavailableDate, isBrokeredBooking = false }: LocationAtCapacityNoticeProps) => {
  const formattedReopenDate = nextAvailableDate ? formatDateMMDDyyyy(nextAvailableDate) : null;
  const formattedUnavailableDate = unavailableDate ? formatDateMMDDyyyy(unavailableDate) : null;

  return (
    <NoticeRoot role="status">
      <EventBusyRounded fontSize={'small'} htmlColor={'#616161'} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span>
        {formattedUnavailableDate
          ? isBrokeredBooking
            ? <>This location is at capacity on <strong>{formattedUnavailableDate}</strong>. This brokered booking will overbook the location.</>
            : <>This location is at capacity on <strong>{formattedUnavailableDate}</strong>. Try different dates or fewer spaces.</>
          : formattedReopenDate
            ? <>This location is at capacity today. Expected to reopen <strong>{formattedReopenDate}</strong>.</>
            : <>This location is at capacity today.</>}
      </span>
    </NoticeRoot>
  );
};

export default LocationAtCapacityNotice;
